import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import {
    Info,
    CheckCircle2,
    Sparkles,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Perx branded insurance premium calculator — replicates the ops mockup.
// All fields, coverage plans and the derived numbers preserve the source
// mockup 1:1; only the visual language is switched to Perx (navy/gold/cream,
// Playfair display, Manrope body, rounded cards, gold accents).
// ---------------------------------------------------------------------------

const AREA_OPTIONS = ["Worldwide", "Asia", "Asia ex-China", "Hong Kong only"];
const NETWORK_OPTIONS = ["Standard", "Premium", "Elite"];
const RESIDENCE_OPTIONS = ["Hong Kong", "Macao", "Singapore", "Taiwan", "Other"];

const HS_PLAN_ROOM = [
    { value: "elite-spr", label: "Elite - SPR" },
    { value: "premier-spr", label: "Premier - SPR" },
    { value: "advantage-semi", label: "Advantage - Semi-private" },
    { value: "essential-ward", label: "Essential - Ward" },
];
const HS_DEDUCTIBLE = ["US$0", "US$500", "US$1,500", "US$5,000"];
const OP_PLAN = ["Essential", "Advantage", "Elite"];
const OP_CAP = ["Nil-7k Cap", "10k Cap", "20k Cap", "Unlimited"];
const DENTAL_PLAN = ["Essential", "Advantage", "Elite"];

// Very simple premium formula. Real business rules would land here — we keep
// the numbers derived-not-authored so any change (add member, tweak plan,
// change age) recomputes without a save-round-trip.
function computeMemberPremium(m) {
    const age = Number(m.age || 0);
    let market = 0;
    if (m.hs_enabled) {
        const baseRoom = { "elite-spr": 6800, "premier-spr": 5400, "advantage-semi": 3600, "essential-ward": 2200 }[m.hs_plan_room] || 4000;
        market += baseRoom + age * 20;
    }
    if (m.op_enabled) {
        const baseOp = { Essential: 4000, Advantage: 5200, Elite: 6800 }[m.op_plan] || 4000;
        const capMult = { "Nil-7k Cap": 1, "10k Cap": 1.15, "20k Cap": 1.3, Unlimited: 1.6 }[m.op_cap] || 1;
        market += baseOp * capMult + age * 5;
    }
    if (m.dental_enabled) {
        const baseD = { Essential: 900, Advantage: 1400, Elite: 2100 }[m.dental_plan] || 900;
        market += baseD;
    }
    const hs_prem = m.hs_enabled ? (market * 0.65 * (m.op_enabled ? 0.62 : 1)) / 1 : 0;
    const op_prem = m.op_enabled ? market - hs_prem - (m.dental_enabled ? 400 : 0) : 0;
    const dental_prem = m.dental_enabled ? 400 : 0;
    const perx_prem = market * 0.85;
    const adjusted = perx_prem * 0.85; // additional welcome discount
    return {
        market_rate: Math.round(market),
        hs_prem: Math.round(hs_prem),
        op_prem: Math.round(op_prem),
        dental_prem: Math.round(dental_prem),
        perx_prem: Math.round(perx_prem * 100) / 100,
        adjusted: Math.round(adjusted * 100) / 100,
    };
}

// Calculator-only coverage selections — these have no backing record, so
// every included member (real or primary) starts from the same defaults.
const COVERAGE_DEFAULTS = {
    area: "Worldwide",
    network: "Standard",
    residence: "Hong Kong",
    hs_enabled: true,
    hs_plan_room: "elite-spr",
    hs_deductible: "US$1,500",
    op_enabled: true,
    op_plan: "Essential",
    op_cap: "Nil-7k Cap",
    dental_enabled: false,
    dental_plan: "Essential",
};

function computeAge(dobIso) {
    if (!dobIso) return "";
    const dob = new Date(dobIso);
    if (Number.isNaN(dob.getTime())) return "";
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
    return age;
}

function buildPrimaryMember(user) {
    const profile = user?.profile || {};
    return {
        id: "primary",
        isPrimary: true,
        name: user?.name || "Primary member",
        age: computeAge(profile.date_of_birth),
        gender: profile.gender || "",
        dob: profile.date_of_birth || "",
        ...COVERAGE_DEFAULTS,
    };
}

function buildMemberFromSub(sub) {
    return {
        id: sub.id,
        isPrimary: false,
        name: sub.name,
        age: sub.age ?? "",
        gender: sub.gender || "",
        dob: sub.dob || "",
        ...COVERAGE_DEFAULTS,
    };
}

export default function Calculator() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dealIdParam = searchParams.get("deal");
    const { user } = useAuth();

    const [subs, setSubs] = useState([]);
    const [members, setMembers] = useState(() => [buildPrimaryMember(null)]);
    const [activeIdx, setActiveIdx] = useState(0);
    const [frequency, setFrequency] = useState("Annual");
    const [busy, setBusy] = useState(false);
    const [isPaidUSD, setIsPaidUSD] = useState(false);

    // Keep the primary row in sync with the logged-in account's own profile
    // (name/dob/gender) once it's loaded, without disturbing any real
    // family members already toggled on.
    useEffect(() => {
        if (!user) return;
        setMembers((ms) => [buildPrimaryMember(user), ...ms.slice(1)]);
    }, [user]);

    useEffect(() => {
        api.get("/member/sub-users")
            .then((r) => setSubs(r.data || []))
            .catch(() => setSubs([]));
    }, []);

    useEffect(() => {
        if (!dealIdParam) return;
        api.get(`/public/deals/${dealIdParam}`)
            .then((r) => setIsPaidUSD(Boolean(r.data?.is_paid_USD)))
            .catch(() => setIsPaidUSD(false));
    }, [dealIdParam]);

    // Eligibility: an adult sub-user counts only while they hold an active
    // membership seat; a minor counts only while the adult who provides for
    // them is currently included in the calculator.
    const eligibleAdults = useMemo(
        () => subs.filter((s) => !s.is_dependant && s.membershipInForce === true),
        [subs]
    );
    const eligibleAdultIds = useMemo(
        () => new Set(eligibleAdults.map((s) => s.id)),
        [eligibleAdults]
    );
    const dependantsByProvider = useMemo(() => {
        const map = new Map();
        subs
            .filter(
                (s) =>
                    s.is_dependant &&
                    s.primary_provider_id &&
                    eligibleAdultIds.has(s.primary_provider_id)
            )
            .forEach((d) => {
                const list = map.get(d.primary_provider_id) || [];
                list.push(d);
                map.set(d.primary_provider_id, list);
            });
        return map;
    }, [subs, eligibleAdultIds]);
    const includedSubIds = useMemo(
        () => new Set(members.filter((m) => !m.isPrimary).map((m) => m.id)),
        [members]
    );

    const currencyLabel = isPaidUSD ? "USD" : "HKD";
    const symbol = isPaidUSD ? "US$" : "$";

    const active = members[activeIdx];
    const setActive = (patch) =>
        setMembers((ms) =>
            ms.map((m, i) => (i === activeIdx ? { ...m, ...patch } : m))
        );

    const summary = useMemo(() => {
        const perMember = members.map((m) => ({ ...m, ...computeMemberPremium(m) }));
        const totalMarket = perMember.reduce((s, m) => s + m.market_rate, 0);
        const totalPerx = perMember.reduce((s, m) => s + m.perx_prem, 0);
        const totalAdjusted = perMember.reduce((s, m) => s + m.adjusted, 0);
        const welcomeDiscount = totalPerx - totalAdjusted;
        return {
            perMember,
            totalMarket,
            totalPerx,
            welcomeDiscount,
            totalAdjusted,
            savings: totalMarket - totalAdjusted,
            quarterly: totalAdjusted / 4,
            semi: totalAdjusted / 2,
        };
    }, [members]);

    const activeCalc = useMemo(
        () => computeMemberPremium(active),
        [active]
    );

    // Checking a sub-user adds them (with their real name/age/gender/dob) to
    // the calculation; unchecking removes them. Unchecking an adult also
    // drops any of their dependants currently included, since a dependant
    // can only be included while their provider is.
    const toggleSub = (sub) => {
        const isIncluded = includedSubIds.has(sub.id);
        setMembers((ms) => {
            if (isIncluded) {
                const cascadeIds = new Set([
                    sub.id,
                    ...(dependantsByProvider.get(sub.id) || []).map((d) => d.id),
                ]);
                return ms.filter((m) => !cascadeIds.has(m.id));
            }
            return [...ms, buildMemberFromSub(sub)];
        });
        setActiveIdx(isIncluded ? 0 : members.length);
    };

    const apply = async () => {
        setBusy(true);
        try {
            const inputs = {
                members: members.map(({ id: _id, ...rest }) => rest),
                frequency,
                summary: {
                    total_market: summary.totalMarket,
                    total_perx: summary.totalPerx,
                    total_adjusted: summary.totalAdjusted,
                    savings: summary.savings,
                },
            };
            if (dealIdParam) {
                await api.post("/member/vendor-cases", {
                    deal_id: dealIdParam,
                    case_type: "calculator_quote",
                    calculator_inputs: inputs,
                    calculator_quote: summary.totalAdjusted,
                    sub_user_ids: Array.from(includedSubIds),
                });
                toast.success(
                    "Sent to vendor — they'll follow up with a firm quote shortly."
                );
                // `resume=calculator` tells the deal page to reopen the
                // purchase workflow past the calculator step (e.g. straight
                // to booking) instead of dropping the member back at a
                // fresh, unstarted page — see WorkflowRunner's `initialStep`.
                navigate(`/member/deals/${dealIdParam}?resume=calculator`);
            } else {
                toast.success(
                    `Quote saved · ${currencyLabel} ${summary.totalAdjusted.toLocaleString()}`
                );
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Insurance"
                title="Coverage selection"
                description="Estimate your Perx-discounted premium across your household. Choose which eligible family members to include below."
                action={
                    <Button
                        onClick={apply}
                        disabled={busy}
                        data-testid="calc-apply"
                        className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-8 font-semibold tracking-widest"
                    >
                        {busy ? "SENDING…" : "APPLY"}
                    </Button>
                }
                testid="calculator-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                {/* ---------- Top summary strip ---------- */}
                <div className="grid lg:grid-cols-2 gap-4">
                    <div className="rounded-2xl bg-white shadow-soft p-5 border border-navy-100/60">
                        <SummaryRow label="Total Market Rate" value={summary.totalMarket} symbol={symbol} />
                        <SummaryRow
                            label="Perx Discounted Premium"
                            value={summary.totalPerx}
                            tone="text-navy-700"
                            symbol={symbol}
                        />
                        <SummaryRow
                            label="Additional welcome discount"
                            value={-summary.welcomeDiscount}
                            tone="text-navy-500"
                            negative
                            symbol={symbol}
                        />
                    </div>
                    <div className="rounded-2xl bg-gradient-to-br from-navy-700 to-navy-800 text-white shadow-soft p-6">
                        <div className="grid grid-cols-2 gap-6 items-center">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-gold-500">
                                    Total Perx discounted premium
                                </div>
                                <div
                                    className="font-display text-4xl mt-2 leading-none"
                                    data-testid="calc-total-perx"
                                >
                                    {currencyLabel} {summary.totalAdjusted.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-gold-500">
                                    You save with us
                                </div>
                                <div
                                    className="font-display text-3xl mt-2 leading-none text-emerald-300"
                                    data-testid="calc-savings"
                                >
                                    {currencyLabel} {summary.savings.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 grid grid-cols-3 gap-2">
                            {[
                                { label: "Quarterly", value: summary.quarterly },
                                { label: "Semi-Annual", value: summary.semi },
                                { label: "Annual", value: summary.totalAdjusted },
                            ].map((f) => {
                                const selected = frequency === f.label;
                                return (
                                    <button
                                        key={f.label}
                                        type="button"
                                        data-testid={`calc-freq-${f.label.toLowerCase()}`}
                                        onClick={() => setFrequency(f.label)}
                                        className={`rounded-lg p-3 text-left transition-colors ${
                                            selected
                                                ? "bg-gold-500 text-navy-900"
                                                : "bg-white/10 hover:bg-white/20 text-navy-50"
                                        }`}
                                    >
                                        <div className="text-[10px] uppercase tracking-widest opacity-80">
                                            {f.label}
                                        </div>
                                        <div className="font-display text-lg mt-1">
                                            {symbol}{f.value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* ---------- Body — sidebar + form ---------- */}
                <div className="grid lg:grid-cols-[280px_1fr] gap-6 items-start">
                    {/* Left sidebar — enrolled members */}
                    <aside className="space-y-4">
                        <div className="rounded-2xl bg-cream border border-gold-200/60 p-4 flex gap-3">
                            <Info className="text-gold-600 shrink-0 mt-0.5" size={16} />
                            <p className="text-[11px] text-navy-700 leading-relaxed">
                                Adults with an active membership can be included below.
                                A child under 18 can only be included once the parent who
                                provides for them is included too.
                            </p>
                        </div>

                        {summary.perMember.map((m, i) => {
                            const selected = activeIdx === i;
                            return (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => setActiveIdx(i)}
                                    data-testid={`calc-member-tab-${i}`}
                                    className={`w-full text-left rounded-2xl p-4 border transition-all ${
                                        selected
                                            ? "bg-white border-gold-500 shadow-soft"
                                            : "bg-white/70 border-navy-100 hover:border-navy-300"
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <div className="font-display text-base text-navy-700 leading-tight">
                                                {m.name || `Member ${i + 1}`}
                                                {m.isPrimary && (
                                                    <span className="text-[10px] uppercase tracking-widest text-gold-600 ml-2 align-middle">
                                                        You
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-navy-500 mt-0.5">
                                                Age: {m.age || "—"} · {(m.gender || "").toLowerCase()}
                                            </div>
                                        </div>
                                        <CheckCircle2 className="text-emerald-500 shrink-0" size={16} />
                                    </div>
                                    <div className="text-[11px] text-navy-500 mt-3 space-y-0.5">
                                        <div>
                                            Total Market Rate:{" "}
                                            <span className="text-navy-700">
                                                {symbol}{m.market_rate.toLocaleString()}
                                            </span>
                                        </div>
                                        <div>
                                            Total Perx Premium:{" "}
                                            <span className="text-navy-700">
                                                {symbol}{m.perx_prem.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                        <div>
                                            Total Perx Adjusted Premium:{" "}
                                            <span className="text-emerald-700 font-semibold">
                                                {symbol}{m.adjusted.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}

                        {eligibleAdults.length > 0 && (
                            <div className="rounded-2xl bg-white border border-navy-100 p-4 space-y-2.5">
                                <div className="text-[10px] uppercase tracking-widest text-navy-500 mb-1">
                                    Eligible family members
                                </div>
                                {eligibleAdults.map((adult) => {
                                    const adultChecked = includedSubIds.has(adult.id);
                                    const dependants = dependantsByProvider.get(adult.id) || [];
                                    return (
                                        <div key={adult.id}>
                                            <label
                                                htmlFor={`calc-sub-${adult.id}`}
                                                className="flex items-center gap-2 text-sm text-navy-700 cursor-pointer"
                                            >
                                                <input
                                                    id={`calc-sub-${adult.id}`}
                                                    type="checkbox"
                                                    data-testid={`calc-sub-check-${adult.id}`}
                                                    checked={adultChecked}
                                                    onChange={() => toggleSub(adult)}
                                                    className="accent-navy-700 h-4 w-4"
                                                />
                                                <span>
                                                    {adult.name}{" "}
                                                    <span className="text-xs text-navy-400">
                                                        · {adult.relationship}
                                                    </span>
                                                </span>
                                            </label>
                                            {dependants.map((dep) => {
                                                const depChecked = includedSubIds.has(dep.id);
                                                return (
                                                    <label
                                                        key={dep.id}
                                                        htmlFor={`calc-sub-${dep.id}`}
                                                        className={`ml-6 flex items-center gap-2 text-xs mt-1.5 ${
                                                            adultChecked
                                                                ? "text-navy-600 cursor-pointer"
                                                                : "text-navy-300 cursor-not-allowed"
                                                        }`}
                                                    >
                                                        <input
                                                            id={`calc-sub-${dep.id}`}
                                                            type="checkbox"
                                                            data-testid={`calc-sub-check-${dep.id}`}
                                                            checked={depChecked}
                                                            disabled={!adultChecked}
                                                            onChange={() => toggleSub(dep)}
                                                            className="accent-navy-700 h-3.5 w-3.5"
                                                        />
                                                        <span>
                                                            {dep.name}{" "}
                                                            <span className={adultChecked ? "text-navy-400" : "text-navy-300"}>
                                                                · Dependant
                                                            </span>
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </aside>

                    {/* Right — active member form */}
                    <div className="space-y-6">
                        {/* Basic information */}
                        <Card>
                            <SectionTitle>Basic information</SectionTitle>
                            <div className="grid sm:grid-cols-3 gap-4">
                                <FormField label="Age">
                                    <div className="h-10 flex items-center text-sm text-navy-700" data-testid="calc-age">
                                        {active.age || "—"}
                                    </div>
                                </FormField>
                                <FormField label="Gender">
                                    <div className="h-10 flex items-center text-sm text-navy-700 capitalize" data-testid="calc-gender">
                                        {active.gender || "—"}
                                    </div>
                                </FormField>
                                <FormField label="Date of Birth">
                                    <div className="h-10 flex items-center text-sm text-navy-700" data-testid="calc-dob">
                                        {active.dob || "—"}
                                    </div>
                                </FormField>
                                <FormField label="Area" required>
                                    <Select
                                        value={active.area}
                                        onValueChange={(v) => setActive({ area: v })}
                                    >
                                        <SelectTrigger data-testid="calc-area">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {AREA_OPTIONS.map((o) => (
                                                <SelectItem key={o} value={o}>
                                                    {o}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <FormField label="Network" required>
                                    <Select
                                        value={active.network}
                                        onValueChange={(v) => setActive({ network: v })}
                                    >
                                        <SelectTrigger data-testid="calc-network">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {NETWORK_OPTIONS.map((o) => (
                                                <SelectItem key={o} value={o}>
                                                    {o}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <FormField label="Residence" required>
                                    <Select
                                        value={active.residence}
                                        onValueChange={(v) => setActive({ residence: v })}
                                    >
                                        <SelectTrigger data-testid="calc-residence">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {RESIDENCE_OPTIONS.map((o) => (
                                                <SelectItem key={o} value={o}>
                                                    {o}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                            </div>
                        </Card>

                        {/* Coverage */}
                        <SectionTitle>Coverage</SectionTitle>
                        <div className="grid md:grid-cols-2 gap-4">
                            <CoverageCard
                                testid="cov-hs"
                                title="Hospital & Surgery"
                                checked={active.hs_enabled}
                                onCheckedChange={(v) => setActive({ hs_enabled: v })}
                            >
                                <div className="grid grid-cols-2 gap-3">
                                    <FormField label="Plan & Room" required small>
                                        <Select
                                            value={active.hs_plan_room}
                                            onValueChange={(v) => setActive({ hs_plan_room: v })}
                                            disabled={!active.hs_enabled}
                                        >
                                            <SelectTrigger data-testid="cov-hs-plan">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {HS_PLAN_ROOM.map((o) => (
                                                    <SelectItem key={o.value} value={o.value}>
                                                        {o.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormField>
                                    <FormField label="Deductible" required small>
                                        <Select
                                            value={active.hs_deductible}
                                            onValueChange={(v) => setActive({ hs_deductible: v })}
                                            disabled={!active.hs_enabled}
                                        >
                                            <SelectTrigger data-testid="cov-hs-deductible">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {HS_DEDUCTIBLE.map((o) => (
                                                    <SelectItem key={o} value={o}>
                                                        {o}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormField>
                                </div>
                                <PremiumPill
                                    label="Premium"
                                    value={active.hs_enabled ? activeCalc.hs_prem : 0}
                                    symbol={symbol}
                                />
                            </CoverageCard>

                            <CoverageCard
                                testid="cov-op"
                                title="Outpatient"
                                checked={active.op_enabled}
                                onCheckedChange={(v) => setActive({ op_enabled: v })}
                            >
                                <div className="grid grid-cols-2 gap-3">
                                    <FormField label="Plan" required small>
                                        <Select
                                            value={active.op_plan}
                                            onValueChange={(v) => setActive({ op_plan: v })}
                                            disabled={!active.op_enabled}
                                        >
                                            <SelectTrigger data-testid="cov-op-plan">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {OP_PLAN.map((o) => (
                                                    <SelectItem key={o} value={o}>
                                                        {o}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormField>
                                    <FormField label="Cap" required small>
                                        <Select
                                            value={active.op_cap}
                                            onValueChange={(v) => setActive({ op_cap: v })}
                                            disabled={!active.op_enabled}
                                        >
                                            <SelectTrigger data-testid="cov-op-cap">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {OP_CAP.map((o) => (
                                                    <SelectItem key={o} value={o}>
                                                        {o}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormField>
                                </div>
                                <PremiumPill
                                    label="Premium"
                                    value={active.op_enabled ? activeCalc.op_prem : 0}
                                    symbol={symbol}
                                />
                            </CoverageCard>

                            <CoverageCard
                                testid="cov-dental"
                                title="Dental"
                                checked={active.dental_enabled}
                                onCheckedChange={(v) =>
                                    setActive({ dental_enabled: v })
                                }
                                className="md:col-span-2 md:max-w-[calc(50%-8px)]"
                            >
                                <FormField label="Plan" small>
                                    <Select
                                        value={active.dental_plan}
                                        onValueChange={(v) => setActive({ dental_plan: v })}
                                        disabled={!active.dental_enabled}
                                    >
                                        <SelectTrigger data-testid="cov-dental-plan">
                                            <SelectValue placeholder="Plan *" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {DENTAL_PLAN.map((o) => (
                                                <SelectItem key={o} value={o}>
                                                    {o}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <PremiumPill
                                    label="Premium"
                                    value={active.dental_enabled ? activeCalc.dental_prem : 0}
                                    symbol={symbol}
                                />
                            </CoverageCard>
                        </div>

                        <div className="rounded-xl bg-navy-700/5 border border-navy-100 p-4 text-[11px] text-navy-500 flex gap-2 items-start">
                            <Sparkles className="text-gold-600 shrink-0 mt-0.5" size={14} />
                            <span>
                                Numbers refresh instantly as you tweak coverage. When you tap{" "}
                                <span className="text-navy-700 font-semibold">APPLY</span>, a
                                case is raised with the vendor including these inputs so they
                                can issue a firm quote.
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

/* ------------------------------ atoms ------------------------------ */

function SummaryRow({ label, value, tone = "text-navy-700", negative, symbol = "$" }) {
    const shown = Math.abs(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return (
        <div className="flex items-center justify-between border-b last:border-b-0 border-navy-50 py-2.5">
            <div className="text-xs text-navy-500 uppercase tracking-widest">
                {label}
            </div>
            <div className={`font-display text-lg ${tone}`}>
                {negative ? "-" : ""}{symbol}{shown}
            </div>
        </div>
    );
}

function Card({ children }) {
    return (
        <div className="rounded-2xl bg-white shadow-soft p-6 border border-navy-100/60">
            {children}
        </div>
    );
}
function SectionTitle({ children }) {
    return (
        <h2 className="font-display text-xl text-navy-700 mb-4">{children}</h2>
    );
}
function FormField({ label, required, small, children }) {
    return (
        <div>
            <Label
                className={`text-navy-700 uppercase tracking-widest ${small ? "text-[10px]" : "text-xs"}`}
            >
                {label}
                {required && <span className="text-gold-600 ml-1">*</span>}
            </Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
function CoverageCard({ title, checked, onCheckedChange, children, testid, className = "" }) {
    return (
        <div
            data-testid={testid}
            className={`rounded-2xl bg-white shadow-soft border p-5 space-y-4 ${
                checked ? "border-gold-400" : "border-navy-100"
            } ${className}`}
        >
            <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                    checked={checked}
                    onCheckedChange={(v) => onCheckedChange(Boolean(v))}
                    data-testid={`${testid}-check`}
                    className="data-[state=checked]:bg-navy-700 data-[state=checked]:border-navy-700"
                />
                <span className="font-display text-lg text-navy-700">{title}</span>
            </label>
            <div className={checked ? "" : "opacity-50 pointer-events-none"}>
                {children}
            </div>
        </div>
    );
}
function PremiumPill({ label, value, symbol = "$" }) {
    return (
        <div className="rounded-lg bg-cream border border-gold-200/60 p-3 mt-3">
            <div className="text-[10px] uppercase tracking-widest text-navy-500">
                {label}
            </div>
            <div className="font-display text-xl text-navy-700 mt-0.5">
                {symbol}{value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
        </div>
    );
}
